import React, { useEffect, useMemo, useState } from 'react';
import { Box, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RESOURCES_PAGE_NAME_TO_LABEL_KEY } from '../../common/constants';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { useCMSContent } from '../../common/hooks/useCmsContent';
import { Tabs, TabVariant } from '../../components/molecules/Tabs/Tabs';
import { PdfDownloadToast } from '../../components/molecules/Toast/PdfDownloadToast';
import { updateGlobalLoaderState } from '../../features/global/globalSlice';
import { getFileFromStore, PdfDownloadToastState } from '../../utils';
import { CardList, DocumentCard, VideoCard } from './ArticleCards';
import { FaqSection } from './FaqSection';
import { formatDocumentDate, getBadgeLabels, getResourceDocuments, ResourceDocument } from './resourcesApi';

const PageContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(4),
    padding: theme.spacing(4, 7)
}));

const Section = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: '100%',
    maxWidth: '1072px'
}));

const HeadingBody = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
}));

const SectionHeading = styled('h2')(({ theme }) => ({
    margin: 0,
    fontFamily: '"TT Commons Pro"',
    fontSize: '30px',
    fontWeight: 700,
    lineHeight: '38px',
    color: theme.palette.primary[800]
}));

const SectionBody = styled('p')(({ theme }) => ({
    margin: 0,
    fontFamily: '"TT Commons Pro"',
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: '25px',
    color: theme.palette.semantic.text.default
}));

const ContentRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(4),
    width: '100%'
}));

const LinkGroup = styled('ul')({
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    width: '200px',
    maxHeight: '531px',
    overflowY: 'auto',
    overflowX: 'clip'
});

const LinkRow = styled('button', { shouldForwardProp: (prop) => prop !== 'selected' })<{
    selected: boolean;
}>(({ theme, selected }) => ({
    appearance: 'none',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: '40px',
    padding: theme.spacing(1),
    border: 'none',
    borderRadius: '6px',
    backgroundColor: selected ? theme.palette.semantic.bg.brandLight1 : 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: '"TT Commons Pro"',
    fontSize: '14px',
    fontWeight: selected ? 700 : 400,
    lineHeight: '19px',
    color: theme.palette.semantic.int.selected,
    textDecoration: 'underline',
    textUnderlinePosition: 'from-font',
    '&:hover': {
        backgroundColor: selected ? theme.palette.semantic.bg.brandLight1 : theme.palette.primary[25]
    },
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.semantic.int.focused}`,
        outlineOffset: '2px'
    }
}));

const EmptyStateMessage = styled('p')(({ theme }) => ({
    boxSizing: 'border-box',
    width: '100%',
    margin: 0,
    padding: theme.spacing(4),
    border: `1px dashed ${theme.palette.semantic.bd.default}`,
    borderRadius: '12px',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '22px',
    textAlign: 'center',
    color: theme.palette.semantic.text.subtle
}));

const TabsColumn = styled(Box)({
    flex: '1 1 0',
    minWidth: 0
});

const RESOURCE_PAGE_NAMES = Object.keys(RESOURCES_PAGE_NAME_TO_LABEL_KEY);

const RESOURCES_OVERVIEW_CONTENT_KEY = 'resources.overview.content';

const Resources = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const cmsData = useAppSelector((state) => state.global?.cmsData);
    const cmsUtil = useCMSContent();
    const overviewContent = cmsData?.[RESOURCES_OVERVIEW_CONTENT_KEY]
        ? cmsUtil.getContentAsElement(RESOURCES_OVERVIEW_CONTENT_KEY)
        : '';
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const currentLanguage = useAppSelector((state) => state?.global?.currentLanguage);
    const [selectedPageName, setSelectedPageName] = useState<string>(RESOURCE_PAGE_NAMES[0]);
    const [tabIndex, setTabIndex] = useState('0');
    const [documents, setDocuments] = useState<ResourceDocument[]>([]);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
    const [pdfToast, setPdfToast] = useState<PdfDownloadToastState>({ open: false, generating: false });

    useEffect(() => {
        if (!tenant) return;
        const fetchDocuments = async () => {
            dispatch(updateGlobalLoaderState(true));
            setIsLoadingDocuments(true);
            try {
                setDocuments(await getResourceDocuments(tenant));
            } catch (error) {
                console.error('Failed to fetch resource documents:', error);
                setDocuments([]);
            } finally {
                setIsLoadingDocuments(false);
                dispatch(updateGlobalLoaderState(false));
            }
        };
        fetchDocuments();
    }, [dispatch, tenant]);

    const pageDocuments = useMemo(
        () =>
            documents
                .filter(
                    (doc) =>
                        doc.resourceType === 'document' &&
                        doc.language === currentLanguage &&
                        doc.pageName === selectedPageName
                )
                .sort((a, b) => a.order - b.order),
        [documents, currentLanguage, selectedPageName]
    );

    const videoDocuments = useMemo(
        () =>
            documents
                .filter(
                    (doc) =>
                        doc.resourceType === 'video' &&
                        doc.language === currentLanguage &&
                        doc.pageName === selectedPageName
                )
                .sort((a, b) => a.order - b.order),
        [documents, currentLanguage, selectedPageName]
    );

    const handleDocumentClick = async (doc: ResourceDocument) => {
        setPdfToast({ open: true, generating: true });
        try {
            await getFileFromStore(tenant, doc.filePath, `${doc.title}.pdf`, setPdfToast);
        } finally {
            setPdfToast((prev) => (prev.generating ? { open: false, generating: false } : prev));
        }
    };

    const hasNoReferences = !isLoadingDocuments && pageDocuments.length === 0 && videoDocuments.length === 0;

    const tabPanels = [
        {
            enabled: true,
            title: t('RESOURCES_TAB_REFERENCES'),
            hidden: false,
            children: hasNoReferences ? (
                <EmptyStateMessage>{t('RESOURCES_REFERENCES_EMPTY')}</EmptyStateMessage>
            ) : (
                <CardList>
                    {pageDocuments.length !== 0 && (
                        pageDocuments.map((doc) => (
                            <DocumentCard
                                key={doc._id}
                                {...getBadgeLabels(doc.tag, t)}
                                title={doc.title}
                                date={formatDocumentDate(doc.fileUpdatedDate)}
                                description={doc.description}
                                onClick={() => handleDocumentClick(doc)}
                            />
                        ))
                    )}

                    {videoDocuments.length !== 0 && (
                        videoDocuments.map((doc) => (
                            <VideoCard
                                key={doc._id}
                                typeLabel={t('RESOURCES_BADGE_TUTORIAL')}
                                formatLabel={t('RESOURCES_BADGE_VIDEO')}
                                title={doc.title}
                                duration={doc.duration || '4 minutes'}
                                description={doc.description}
                            />
                        ))
                    )}
                </CardList>
            )
        },
        {
            enabled: true,
            title: t('RESOURCES_TAB_FAQ'),
            hidden: false,
            children: <FaqSection key={selectedPageName} pageName={selectedPageName} />
        }
    ];

    const handleTabChange = (_event: React.SyntheticEvent, value: string) => setTabIndex(value);

    return (
        <PageContainer>
            <Section>
                <HeadingBody>
                    <SectionHeading as="h1">{t('RESOURCES_OVERVIEW')}</SectionHeading>
                    <SectionBody as="div">{overviewContent}</SectionBody>
                </HeadingBody>
            </Section>
            <Section>
                <SectionHeading>{t('RESOURCES_REFERENCE_DOCUMENTS')}</SectionHeading>
                <ContentRow>
                    <LinkGroup aria-label={t('RESOURCES_REFERENCE_DOCUMENTS')}>
                        {RESOURCE_PAGE_NAMES.map((pageName) => (
                            <li key={pageName}>
                                <LinkRow
                                    type="button"
                                    selected={selectedPageName === pageName}
                                    aria-current={selectedPageName === pageName ? 'true' : undefined}
                                    onClick={() => setSelectedPageName(pageName)}
                                >
                                    {t(RESOURCES_PAGE_NAME_TO_LABEL_KEY[pageName])}
                                </LinkRow>
                            </li>
                        ))}
                    </LinkGroup>
                    <TabsColumn>
                        <Tabs
                            tabPanels={tabPanels}
                            value={tabIndex}
                            onChange={handleTabChange}
                            variant={TabVariant.ENCLOSED_TAB}
                            panelSx={{ px: 0, pb: 0 }}
                        />
                    </TabsColumn>
                </ContentRow>
            </Section>
            {pdfToast.open && (
                <PdfDownloadToast state={pdfToast} onClose={() => setPdfToast({ open: false, generating: false })} />
            )}
        </PageContainer>
    );
};

export default Resources;
